import {
  BankAccount,
  CreditCard,
  Investment,
  CryptoHolding,
  PrivateEquity,
  HSA,
  CreditScore,
  Asset,
  Liability,
  Retirement401k,
  NetWorthSnapshot,
  BudgetCategory,
  Goal,
  MarketIndex,
  RetirementSettings,
  UserSettings,
  DashboardView,
  CheckIn,
} from '../types';
import {
  SHEET_COLORS,
  NET_WORTH_HISTORY_HEADERS,
  BUDGET_HEADERS,
  GOALS_HEADERS,
  CRYPTO_HEADERS,
  HSA_HEADERS,
  CREDIT_SCORE_HEADERS,
  PRIVATE_EQUITY_HEADERS,
  DEFAULT_BUDGET_CATEGORIES,
  MARKET_SNAPSHOT_HEADERS,
  MARKET_SNAPSHOT_TICKERS,
  RETIREMENT_SETTINGS_HEADERS,
  USER_SETTINGS_HEADERS,
  CHECK_INS_HEADERS,
  DEFAULT_DASHBOARD_VIEW,
  INVESTMENT_HEADERS,
  buildInvestmentPeriodReturnFormulas,
} from '../config/constants';
import { parseCurrency } from '../utils/formatters';

/** Parse a sheet percent cell (1.5, "1.5%", #N/A → null). */
const parseSheetPercent = (raw: string | undefined): number | null => {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (!s || s.startsWith('#')) return null;
  const n = parseFloat(s.replace(/%/g, '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};

/** Build GOOGLEFINANCE seed rows for the Market Snapshot tab. */
const buildMarketSnapshotSeed = () =>
  MARKET_SNAPSHOT_TICKERS.map((t, i) => {
    const row = i + 2;
    return [
      t.symbol,
      t.label,
      `=IFERROR(GOOGLEFINANCE("${t.gfSymbol}","price"),0)`,
      `=IFERROR(GOOGLEFINANCE("${t.gfSymbol}","changepct")/100*C${row},0)`,
      `=IFERROR(GOOGLEFINANCE("${t.gfSymbol}","changepct"),0)`,
    ];
  });

// --- Security: Input sanitization for Google Sheets ---

/**
 * Sanitize string input to prevent Google Sheets formula injection.
 * Strips leading characters that Sheets interprets as formula markers (= + - @).
 */
function sanitizeSheetString(value: string): string {
  return value.replace(/^[=+\-@]+/, '').slice(0, 200);
}

/**
 * Validate a stock ticker symbol — only allow letters, digits, and dots (e.g., BRK.B).
 */
function validateSymbol(symbol: string): string {
  const cleaned = symbol.replace(/[^A-Za-z0-9.]/g, '').toUpperCase();
  if (!cleaned || cleaned.length > 10) throw new Error('Invalid ticker symbol');
  return cleaned;
}

function validateCryptoId(id: string): string {
  const cleaned = id.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase();
  if (cleaned.length > 60) throw new Error('Invalid CoinGecko ID');
  return cleaned;
}

// --- Token refresh on 401: auto-retry API calls with a fresh token ---

let tokenRefresher: (() => Promise<void>) | null = null;

/**
 * Register a callback that acquires a fresh Google token.
 * Called by AuthContext on mount so the service layer can self-heal on 401.
 */
export const setTokenRefresher = (refresher: () => Promise<void>): void => {
  tokenRefresher = refresher;
};

/**
 * Wrap a Sheets API call: if it fails with 401, refresh the token and retry once.
 */
async function withTokenRefresh<T>(apiCall: () => Promise<T>): Promise<T> {
  try {
    return await apiCall();
  } catch (err: unknown) {
    const status =
      (err as { status?: number })?.status ||
      (err as { result?: { error?: { code?: number } } })?.result?.error?.code;
    if (status === 401 && tokenRefresher) {
      await tokenRefresher();
      return await apiCall();
    }
    throw err;
  }
}

// --- Tab verification cache: skip ensureAllTabs on refresh for established users ---
let verifiedSheetId: string | null = null;

/**
 * Reset the tab verification flag. Call on sign-out so the next session
 * does a fresh ensureAllTabs check.
 */
export const resetTabVerification = (): void => {
  verifiedSheetId = null;
};

/**
 * Append a net worth snapshot to the next empty row. Sheet grows automatically
 * if it has reached its row limit. Caller is responsible for verifying the date
 * doesn't already exist.
 */
export const appendNetWorthSnapshot = async (
  sheetId: string,
  snapshot: NetWorthSnapshot,
): Promise<void> => {
  await window.gapi.client.sheets.spreadsheets.values.append({
    spreadsheetId: sheetId,
    range: `'Net Worth History'!A:I`,
    valueInputOption: 'RAW',
    insertDataOption: 'INSERT_ROWS',
    resource: {
      values: [[
        snapshot.date,
        snapshot.netWorth,
        snapshot.totalAssets,
        snapshot.totalLiabilities,
        snapshot.banking,
        snapshot.investments,
        snapshot.retirement401k,
        snapshot.creditCards,
        snapshot.crypto || 0,
      ]],
    },
  });
};

/**
 * Write the snapshot for its date: overwrite that date's row if it exists,
 * otherwise append. Keeps one row per day that reflects the latest load.
 */
export const upsertNetWorthSnapshot = async (
  sheetId: string,
  snapshot: NetWorthSnapshot,
): Promise<void> => {
  const dates = await withTokenRefresh(() =>
    window.gapi.client.sheets.spreadsheets.values.batchGet({
      spreadsheetId: sheetId,
      ranges: [`'Net Worth History'!A:A`],
    })
  );
  const column: string[][] = dates.result.valueRanges?.[0]?.values || [];
  const rowIndex = column.findIndex((row, i) => i > 0 && row[0] === snapshot.date);
  if (rowIndex < 0) {
    await withTokenRefresh(() => appendNetWorthSnapshot(sheetId, snapshot));
    return;
  }
  const row = rowIndex + 1;
  await withTokenRefresh(() =>
    window.gapi.client.sheets.spreadsheets.values.update({
      spreadsheetId: sheetId,
      range: `'Net Worth History'!A${row}:I${row}`,
      valueInputOption: 'RAW',
      resource: {
        values: [[
          snapshot.date,
          snapshot.netWorth,
          snapshot.totalAssets,
          snapshot.totalLiabilities,
          snapshot.banking,
          snapshot.investments,
          snapshot.retirement401k,
          snapshot.creditCards,
          snapshot.crypto || 0,
        ]],
      },
    })
  );
};

/**
 * Record a check-in. Callers only append when the date isn't already in their
 * loaded history; duplicates from two open tabs are collapsed when parsing.
 */
export const appendCheckIn = async (sheetId: string, checkIn: CheckIn): Promise<void> => {
  await withTokenRefresh(() =>
    window.gapi.client.sheets.spreadsheets.values.append({
      spreadsheetId: sheetId,
      range: `'Check-ins'!A:B`,
      valueInputOption: 'RAW',
      insertDataOption: 'INSERT_ROWS',
      resource: { values: [[checkIn.date, checkIn.source]] },
    })
  );
};

/**
 * Check if a spreadsheet exists and is accessible
 * @param sheetId - The spreadsheet ID to check
 * @returns true if exists and accessible, false only if confirmed deleted (404)
 */
export const checkSheetExists = async (sheetId: string): Promise<boolean> => {
  try {
    await window.gapi.client.sheets.spreadsheets.get({
      spreadsheetId: sheetId,
    });
    return true;
  } catch (error: unknown) {
    const apiError = error as { status?: number; result?: { error?: { code?: number } } };
    const status = apiError.status || apiError.result?.error?.code;

    if (status === 404) {
      return false;
    }

    // For any other error (auth, network, etc.), assume sheet exists
    // to prevent accidentally creating duplicate sheets
    console.warn('Error checking sheet, assuming it exists:', error);
    return true;
  }
};

/**
 * Ensure all required tabs exist in the spreadsheet, creating any missing ones.
 * Uses a single spreadsheets.get() call and batches all creation into one request.
 * Returns the list of tab names so callers can skip a redundant metadata fetch.
 * Throws on 404 (sheet deleted) so the caller can handle recreation.
 */
export const ensureAllTabs = async (
  sheetId: string,
  tabConfigs: Array<{ name: string; headers: string[]; rowCount?: number }>
): Promise<string[]> => {
  const metadata = await withTokenRefresh(() =>
    window.gapi.client.sheets.spreadsheets.get({
      spreadsheetId: sheetId,
    })
  );

  const existingTabs = metadata.result.sheets?.map(
    (s: { properties?: { title?: string } }) => s.properties?.title || ''
  ) || [];

  const missingConfigs = tabConfigs.filter(tc => !existingTabs.includes(tc.name));

  if (missingConfigs.length === 0) {
    return existingTabs;
  }

  // Create all missing tabs in a single batchUpdate
  await withTokenRefresh(() =>
    window.gapi.client.sheets.spreadsheets.batchUpdate({
      spreadsheetId: sheetId,
      resource: {
        requests: missingConfigs.map(tc => ({
          addSheet: {
            properties: {
              title: tc.name,
              gridProperties: { rowCount: tc.rowCount || 100, columnCount: 10 },
            },
          },
        })),
      },
    })
  );

  // Write all headers in a single batchUpdate
  await withTokenRefresh(() =>
    window.gapi.client.sheets.spreadsheets.values.batchUpdate({
      spreadsheetId: sheetId,
      resource: {
        valueInputOption: 'RAW',
        data: missingConfigs.map(tc => ({
          range: `'${tc.name}'!A1:${String.fromCharCode(64 + tc.headers.length)}1`,
          values: [tc.headers],
        })),
      },
    })
  );

  // Style headers for newly created tabs
  const updatedMetadata = await withTokenRefresh(() =>
    window.gapi.client.sheets.spreadsheets.get({
      spreadsheetId: sheetId,
    })
  );
  const styleRequests: unknown[] = [];
  for (const tc of missingConfigs) {
    const sheet = updatedMetadata.result.sheets?.find(
      (s: { properties?: { title?: string; sheetId?: number } }) => s.properties?.title === tc.name
    );
    if (sheet?.properties?.sheetId !== undefined) {
      styleRequests.push(
        {
          repeatCell: {
            range: { sheetId: sheet.properties.sheetId, startRowIndex: 0, endRowIndex: 1 },
            cell: {
              userEnteredFormat: {
                backgroundColor: SHEET_COLORS.purpleHeader,
                textFormat: {
                  foregroundColor: SHEET_COLORS.white,
                  fontSize: 11,
                  bold: true,
                },
                horizontalAlignment: 'LEFT',
              },
            },
            fields: 'userEnteredFormat(backgroundColor,textFormat,horizontalAlignment)',
          },
        },
        {
          autoResizeDimensions: {
            dimensions: {
              sheetId: sheet.properties.sheetId,
              dimension: 'COLUMNS',
              startIndex: 0,
              endIndex: tc.headers.length,
            },
          },
        }
      );
    }
  }

  if (styleRequests.length > 0) {
    await withTokenRefresh(() =>
      window.gapi.client.sheets.spreadsheets.batchUpdate({
        spreadsheetId: sheetId,
        resource: { requests: styleRequests },
      })
    );
  }

  const allTabs = updatedMetadata.result.sheets?.map(
    (s: { properties?: { title?: string } }) => s.properties?.title || ''
  ) || [];

  return allTabs;
};

/**
 * Create a new TallyUp spreadsheet template
 * @returns The new spreadsheet ID
 */
export const createTemplateSheet = async (): Promise<string> => {

  const createResponse = await window.gapi.client.sheets.spreadsheets.create({
    properties: {
      title: `TallyUp - ${new Date().toLocaleDateString()}`,
    },
    sheets: [
      { properties: { title: 'README', gridProperties: { rowCount: 20, columnCount: 2 } } },
      { properties: { title: 'Banking', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Credit Cards', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Investments', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: '401k', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Assets', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Liabilities', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Net Worth History', gridProperties: { rowCount: 500, columnCount: 10 } } },
      { properties: { title: 'Budget', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Goals', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Crypto', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Private Equity', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'HSA', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Credit Scores', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Market Snapshot', gridProperties: { rowCount: 20, columnCount: 10 } } },
      { properties: { title: 'Retirement Settings', gridProperties: { rowCount: 5, columnCount: 10 } } },
      { properties: { title: 'User Settings', gridProperties: { rowCount: 5, columnCount: 10 } } },
    ],
  });

  const newSheetId = createResponse.result.spreadsheetId;

  // Add headers
  await window.gapi.client.sheets.spreadsheets.values.batchUpdate({
    spreadsheetId: newSheetId,
    resource: {
      valueInputOption: 'RAW',
      data: [
        {
          range: 'README!A1:A12',
          values: [
            ['📊 TallyUp Finance — Your Financial Data'],
            [''],
            ['⚠️ Please do not edit this spreadsheet directly.'],
            [''],
            ['TallyUp reads from and writes to this sheet automatically as you use the app.'],
            ['Manual edits may cause data corruption, broken formulas, or unexpected behavior in the dashboard.'],
            [''],
            ['✅ Use the TallyUp app to add, edit, or delete entries: https://www.tallyup.finance'],
            [''],
            ['🔒 Your data lives here in your Google Drive — TallyUp never sees or stores your information on any server.'],
            [''],
            ['Questions or issues? Submit feedback from Settings in the app, or email support@tallyup.finance'],
          ],
        },
        { range: 'Banking!A1:B1', values: [['Account Name', 'Balance']] },
        { range: 'Credit Cards!A1:C1', values: [['Card Name', 'Balance', 'Limit']] },
        { range: 'Investments!A1:K1', values: [INVESTMENT_HEADERS] },
        { range: "'401k'!A1:G1", values: [['Fund Name', 'Ticker', 'Shares', 'Balance', 'Employer', 'Account Type', 'Cost Basis']] },
        { range: 'Assets!A1:B1', values: [['Asset Name', 'Value']] },
        { range: 'Liabilities!A1:C1', values: [['Liability Name', 'Amount', 'APR %']] },
        { range: "'Net Worth History'!A1:I1", values: [NET_WORTH_HISTORY_HEADERS] },
        { range: 'Budget!A1:D1', values: [BUDGET_HEADERS] },
        { range: 'Goals!A1:E1', values: [GOALS_HEADERS] },
        { range: 'Crypto!A1:E1', values: [CRYPTO_HEADERS] },
        { range: "'Private Equity'!A1:G1", values: [PRIVATE_EQUITY_HEADERS] },
        { range: 'HSA!A1:B1', values: [HSA_HEADERS] },
        { range: "'Credit Scores'!A1:B1", values: [CREDIT_SCORE_HEADERS] },
        { range: "'Market Snapshot'!A1:E1", values: [MARKET_SNAPSHOT_HEADERS] },
        { range: "'Retirement Settings'!A1:J1", values: [RETIREMENT_SETTINGS_HEADERS] },
        { range: "'User Settings'!A1:A1", values: [USER_SETTINGS_HEADERS] },
      ],
    },
  });

  // Add initial data
  await window.gapi.client.sheets.spreadsheets.values.batchUpdate({
    spreadsheetId: newSheetId,
    resource: {
      valueInputOption: 'USER_ENTERED',
      data: [
        {
          range: `'Market Snapshot'!A2:E${MARKET_SNAPSHOT_TICKERS.length + 1}`,
          values: buildMarketSnapshotSeed(),
        },
        {
          range: 'Banking!A2:B4',
          values: [
            ['Checking Account', 0],
            ['Savings Account 1', 0],
            ['Savings Account 2', 0],
          ],
        },
        {
          range: 'Credit Cards!A2:C5',
          values: [
            ['Credit Card 1', 0, 5000],
            ['Credit Card 2', 0, 5000],
            ['Credit Card 3', 0, 5000],
            ['Credit Card 4', 0, 5000],
          ],
        },
        {
          range: 'Investments!A2:G2',
          values: [[]],
        },
        {
          range: 'Assets!A2:B5',
          values: [
            ['Primary Home', 0],
            ['Investment Portfolio', '=SUM(Investments!E:E)'],
            ['Vehicle 1', 0],
            ['Vehicle 2', 0],
          ],
        },
        {
          range: 'Liabilities!A2:C5',
          values: [
            ['Mortgage', 0, 0],
            ['Car Loan', 0, 0],
            ['Student Loans', 0, 0],
            ['Credit Card Debt', "=SUM('Credit Cards'!B:B)", 0],
          ],
        },
      ],
    },
  });

  // Seed default budget categories
  await seedDefaultBudgetCategories(newSheetId, DEFAULT_BUDGET_CATEGORIES);


  // Style the spreadsheet
  await styleTemplateSheet(newSheetId);

  return newSheetId;
};

/**
 * Apply TallyUp styling to a spreadsheet
 * @param spreadsheetId - The spreadsheet ID to style
 */
export const styleTemplateSheet = async (spreadsheetId: string): Promise<void> => {

  const sheetMetadata = await window.gapi.client.sheets.spreadsheets.get({
    spreadsheetId,
  });

  const sheets = sheetMetadata.result.sheets;
  const getSheetId = (title: string) =>
    sheets.find(s => s.properties.title === title)?.properties.sheetId;

  const bankingSheetId = getSheetId('Banking');
  const creditCardsSheetId = getSheetId('Credit Cards');
  const investmentsSheetId = getSheetId('Investments');
  const retirement401kSheetId = getSheetId('401k');
  const assetsSheetId = getSheetId('Assets');
  const liabilitiesSheetId = getSheetId('Liabilities');
  const netWorthHistorySheetId = getSheetId('Net Worth History');
  const budgetSheetId = getSheetId('Budget');
  const goalsSheetId = getSheetId('Goals');
  const cryptoSheetId = getSheetId('Crypto');
  const privateEquitySheetId = getSheetId('Private Equity');
  const hsaSheetId = getSheetId('HSA');

  const createHeaderStyle = (sheetId: number | undefined, color: typeof SHEET_COLORS.purpleHeader) => ({
    repeatCell: {
      range: { sheetId, startRowIndex: 0, endRowIndex: 1 },
      cell: {
        userEnteredFormat: {
          backgroundColor: color,
          textFormat: {
            foregroundColor: SHEET_COLORS.white,
            fontSize: 11,
            bold: true,
          },
          horizontalAlignment: 'LEFT',
        },
      },
      fields: 'userEnteredFormat(backgroundColor,textFormat,horizontalAlignment)',
    },
  });

  const createAutoResize = (sheetId: number | undefined, endIndex: number) => ({
    autoResizeDimensions: {
      dimensions: {
        sheetId,
        dimension: 'COLUMNS',
        startIndex: 0,
        endIndex,
      },
    },
  });

  const requests = [
    createHeaderStyle(bankingSheetId, SHEET_COLORS.purpleHeader),
    createHeaderStyle(creditCardsSheetId, SHEET_COLORS.purpleHeader),
    createHeaderStyle(investmentsSheetId, SHEET_COLORS.pinkAccent),
    ...(retirement401kSheetId !== undefined ? [
      createHeaderStyle(retirement401kSheetId, SHEET_COLORS.pinkAccent),
      createAutoResize(retirement401kSheetId, 7),
    ] : []),
    createHeaderStyle(assetsSheetId, SHEET_COLORS.purpleHeader),
    createHeaderStyle(liabilitiesSheetId, SHEET_COLORS.purpleHeader),
    createAutoResize(bankingSheetId, 2),
    createAutoResize(creditCardsSheetId, 3),
    createAutoResize(investmentsSheetId, 7),
    createAutoResize(assetsSheetId, 2),
    createAutoResize(liabilitiesSheetId, 2),
    ...(netWorthHistorySheetId !== undefined ? [
      createHeaderStyle(netWorthHistorySheetId, SHEET_COLORS.pinkAccent),
      createAutoResize(netWorthHistorySheetId, 8),
    ] : []),
    ...(budgetSheetId !== undefined ? [
      createHeaderStyle(budgetSheetId, SHEET_COLORS.purpleHeader),
      createAutoResize(budgetSheetId, 3),
    ] : []),
    ...(goalsSheetId !== undefined ? [
      createHeaderStyle(goalsSheetId, SHEET_COLORS.purpleHeader),
      createAutoResize(goalsSheetId, 5),
    ] : []),
    ...(cryptoSheetId !== undefined ? [
      createHeaderStyle(cryptoSheetId, SHEET_COLORS.pinkAccent),
      createAutoResize(cryptoSheetId, 5),
    ] : []),
    ...(privateEquitySheetId !== undefined ? [
      createHeaderStyle(privateEquitySheetId, SHEET_COLORS.pinkAccent),
      createAutoResize(privateEquitySheetId, 7),
    ] : []),
    ...(hsaSheetId !== undefined ? [
      createHeaderStyle(hsaSheetId, SHEET_COLORS.purpleHeader),
      createAutoResize(hsaSheetId, 2),
    ] : []),
  ];

  await window.gapi.client.sheets.spreadsheets.batchUpdate({
    spreadsheetId,
    resource: { requests },
  });

};

/**
 * Ensure the Liabilities tab has the APR % header in column C.
 * For existing users whose sheet predates the interest rate feature.
 */
export const ensureLiabilitiesInterestRateColumn = async (sheetId: string): Promise<void> => {
  try {
    const response = await window.gapi.client.sheets.spreadsheets.values.batchGet({
      spreadsheetId: sheetId,
      ranges: ['Liabilities!C1'],
    });
    const headerValue = response.result.valueRanges?.[0]?.values?.[0]?.[0];
    if (!headerValue || headerValue !== 'APR %') {
      await window.gapi.client.sheets.spreadsheets.values.update({
        spreadsheetId: sheetId,
        range: 'Liabilities!C1',
        valueInputOption: 'RAW',
        resource: { values: [['APR %']] },
      });
    }
  } catch (error) {
    console.warn('Could not ensure Liabilities APR % column:', error);
  }
};

/**
 * Load all financial data from a Google Sheet
 * @param sheetId - The spreadsheet ID to load from
 */
export const loadDataFromSheets = async (sheetId: string): Promise<{
  banking: BankAccount[];
  creditCards: CreditCard[];
  investments: Investment[];
  cryptoHoldings: CryptoHolding[];
  retirement401k: Retirement401k[];
  privateEquityHoldings: PrivateEquity[];
  hsaAccounts: HSA[];
  creditScores: CreditScore[];
  marketSnapshot: MarketIndex[];
  assets: Asset[];
  liabilities: Liability[];
  budgetCategories: BudgetCategory[];
  goals: Goal[];
  netWorthHistory: NetWorthSnapshot[];
  retirementSettings: RetirementSettings | null;
  userSettings: UserSettings;
  checkIns: CheckIn[];
}> => {

  // Skip ensureAllTabs if we've already verified this sheet's tabs (saves 1 API call on refresh)
  let has401kTab = true;
  let hasCheckInsTab = true;
  if (verifiedSheetId === sheetId) {
  } else {
    const tabNames = await ensureAllTabs(sheetId, [
      { name: '401k', headers: ['Fund Name', 'Ticker', 'Shares', 'Balance', 'Employer', 'Account Type', 'Cost Basis'] },
      { name: 'Net Worth History', headers: NET_WORTH_HISTORY_HEADERS, rowCount: 500 },
      { name: 'Budget', headers: BUDGET_HEADERS },
      { name: 'Goals', headers: GOALS_HEADERS },
      { name: 'Crypto', headers: CRYPTO_HEADERS },
      { name: 'Private Equity', headers: PRIVATE_EQUITY_HEADERS },
      { name: 'HSA', headers: HSA_HEADERS },
      { name: 'Credit Scores', headers: CREDIT_SCORE_HEADERS },
      { name: 'Market Snapshot', headers: MARKET_SNAPSHOT_HEADERS, rowCount: 20 },
      { name: 'Retirement Settings', headers: RETIREMENT_SETTINGS_HEADERS, rowCount: 5 },
      { name: 'User Settings', headers: USER_SETTINGS_HEADERS, rowCount: 5 },
      { name: 'Check-ins', headers: CHECK_INS_HEADERS, rowCount: 1000 },
    ]);
    has401kTab = tabNames.includes('401k');
    hasCheckInsTab = tabNames.includes('Check-ins');
    verifiedSheetId = sheetId;
  }

  // Build all ranges for a single batchGet — includes Liabilities!C1 for migration check
  const ranges = [
    'Banking!A2:B100',
    'Credit Cards!A2:C100',
    'Investments!A2:K100',
    ...(has401kTab ? ["'401k'!A2:G100"] : []),
    'Assets!A2:B100',
    'Liabilities!A2:C100',
    'Budget!A2:D100',
    'Goals!A2:E100',
    'Crypto!A2:E100',
    "'Private Equity'!A2:G100",
    'HSA!A2:B100',
    "'Credit Scores'!A2:B100",
    "'Net Worth History'!A2:I500",
    "'Market Snapshot'!A2:E20",
    "'Retirement Settings'!A2:J2",
    "'User Settings'!A2:A2",
    'Liabilities!C1',
    'Investments!G1',
    'Investments!H1',
    'Crypto!E1',
    'Goals!E1',
    ...(hasCheckInsTab ? ["'Check-ins'!A2:B"] : []),
  ];

  const response = await withTokenRefresh(() =>
    window.gapi.client.sheets.spreadsheets.values.batchGet({
      spreadsheetId: sheetId,
      ranges,
    })
  );

  const valueRanges = response.result.valueRanges.map(
    (range: { values?: string[][] }) => range?.values || []
  );

  // Destructure based on whether 401k tab exists
  let idx = 0;
  const bankingRows = valueRanges[idx++];
  const creditRows = valueRanges[idx++];
  const investmentRows = valueRanges[idx++];
  const retirement401kRows = has401kTab ? valueRanges[idx++] : [];
  const assetRows = valueRanges[idx++];
  const liabilityRows = valueRanges[idx++];
  const budgetRows = valueRanges[idx++];
  const goalRows = valueRanges[idx++];
  const cryptoRows = valueRanges[idx++];
  const privateEquityRows = valueRanges[idx++];
  const hsaRows = valueRanges[idx++];
  const creditScoreRows = valueRanges[idx++];
  const historyRows = valueRanges[idx++];
  const marketSnapshotRows = valueRanges[idx++];
  const retirementSettingsRows = valueRanges[idx++];
  const userSettingsRows = valueRanges[idx++];

  // Fire-and-forget: ensure Liabilities APR % header exists (migration for older sheets)
  const liabilitiesHeaderValue = valueRanges[idx++]?.[0]?.[0];
  if (!liabilitiesHeaderValue || liabilitiesHeaderValue !== 'APR %') {
    window.gapi.client.sheets.spreadsheets.values.update({
      spreadsheetId: sheetId,
      range: 'Liabilities!C1',
      valueInputOption: 'RAW',
      resource: { values: [['APR %']] },
    }).then(() => {
    }).catch((err: unknown) => {
      console.warn('Could not migrate Liabilities APR % header:', err);
    });
  }

  // Fire-and-forget: ensure Investments Account header exists (migration for older sheets)
  const investmentsAccountHeader = valueRanges[idx++]?.[0]?.[0];
  if (!investmentsAccountHeader || investmentsAccountHeader !== 'Account') {
    window.gapi.client.sheets.spreadsheets.values.update({
      spreadsheetId: sheetId,
      range: 'Investments!G1',
      valueInputOption: 'RAW',
      resource: { values: [['Account']] },
    }).then(() => {
    }).catch((err: unknown) => {
      console.warn('Could not migrate Investments Account header:', err);
    });
  }

  // Ensure Investments period-return columns. 1D (changepct) works via Sheets API;
  // 1W/1M/1Y historical GOOGLEFINANCE cannot be read via API — those are filled client-side.
  const investmentsPeriodHeader = valueRanges[idx++]?.[0]?.[0];
  const needsPeriodBackfill =
    !investmentsPeriodHeader ||
    investmentsPeriodHeader !== '1D %' ||
    investmentRows.some((row: string[]) => {
      if (!row[0]) return false;
      const daily = row[7];
      return daily == null || daily === '' || String(daily).startsWith('#');
    });

  if (needsPeriodBackfill) {
    const periodData = investmentRows.map((row: string[], i: number) =>
      row[0] ? buildInvestmentPeriodReturnFormulas(i + 2) : ['', '', '', '']
    );
    const lastFilled = periodData.reduce((last, row, i) => (row[0] ? i : last), -1);

    try {
      await withTokenRefresh(() =>
        window.gapi.client.sheets.spreadsheets.values.batchUpdate({
          spreadsheetId: sheetId,
          resource: {
            valueInputOption: 'USER_ENTERED',
            data: [
              { range: 'Investments!H1:K1', values: [['1D %', '1W %', '1M %', '1Y %']] },
              ...(lastFilled >= 0
                ? [{
                    range: `Investments!H2:K${lastFilled + 2}`,
                    values: periodData.slice(0, lastFilled + 1),
                  }]
                : []),
            ],
          },
        })
      );
      // Give GOOGLEFINANCE a moment, then re-read 1D % (changepct is API-readable)
      await new Promise(r => setTimeout(r, 2500));
      const refreshed = await withTokenRefresh(() =>
        window.gapi.client.sheets.spreadsheets.values.batchGet({
          spreadsheetId: sheetId,
          ranges: [`Investments!H2:H${Math.max(lastFilled + 2, 2)}`],
        })
      );
      const dailyCols: string[][] = refreshed.result.valueRanges?.[0]?.values || [];
      dailyCols.forEach((cell, i) => {
        if (investmentRows[i]) {
          investmentRows[i][7] = cell[0];
        }
      });
    } catch (err: unknown) {
      console.warn('Could not migrate Investments period return columns:', err);
    }
  }

  // Fire-and-forget: ensure Crypto Account header exists (migration for older sheets)
  const cryptoAccountHeader = valueRanges[idx++]?.[0]?.[0];
  if (!cryptoAccountHeader || cryptoAccountHeader !== 'Account') {
    window.gapi.client.sheets.spreadsheets.values.update({
      spreadsheetId: sheetId,
      range: 'Crypto!E1',
      valueInputOption: 'RAW',
      resource: { values: [['Account']] },
    }).then(() => {
    }).catch((err: unknown) => {
      console.warn('Could not migrate Crypto Account header:', err);
    });
  }

  // Fire-and-forget: ensure Goals Unit header exists (migration for older sheets)
  const goalsUnitHeader = valueRanges[idx++]?.[0]?.[0];
  const checkInRows: string[][] = hasCheckInsTab ? valueRanges[idx++] || [] : [];
  const checkInDates = new Set<string>();
  const checkIns: CheckIn[] = [];
  for (const row of checkInRows) {
    const date = row[0];
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '') || checkInDates.has(date)) continue;
    checkInDates.add(date);
    checkIns.push({ date, source: row[1] === 'confirm' ? 'confirm' : 'edit' });
  }
  if (!goalsUnitHeader || goalsUnitHeader !== 'Unit') {
    window.gapi.client.sheets.spreadsheets.values.update({
      spreadsheetId: sheetId,
      range: 'Goals!E1',
      valueInputOption: 'RAW',
      resource: { values: [['Unit']] },
    }).then(() => {
    }).catch((err: unknown) => {
      console.warn('Could not migrate Goals Unit header:', err);
    });
  }

  const banking: BankAccount[] = bankingRows
    .filter(row => row[0])
    .map((row, i) => ({
      name: row[0],
      balance: parseCurrency(row[1] || '0'),
      editable: true,
      cell: `Banking!B${i + 2}`,
    }));

  const creditCards: CreditCard[] = creditRows
    .filter(row => row[0])
    .map((row, i) => ({
      name: row[0],
      balance: parseCurrency(row[1] || '0'),
      limit: parseCurrency(row[2] || '0'),
      editable: true,
      balanceCell: `Credit Cards!B${i + 2}`,
      limitCell: `Credit Cards!C${i + 2}`,
    }));

  const investments: Investment[] = investmentRows
    .filter(row => row[0])
    .map((row, i) => ({
      symbol: row[0],
      name: row[0],
      shares: parseFloat(row[1] || '0'),
      costBasis: parseCurrency(row[2] || '0'),
      currentPrice: parseCurrency(row[3] || '0'),
      totalValue: parseCurrency(row[4] || '0'),
      account: row[6] || 'Individual',
      dailyReturn: parseSheetPercent(row[7]),
      weeklyReturn: parseSheetPercent(row[8]),
      monthlyReturn: parseSheetPercent(row[9]),
      annualReturn: parseSheetPercent(row[10]),
      editable: true,
      symbolCell: `Investments!A${i + 2}`,
      sharesCell: `Investments!B${i + 2}`,
      costBasisCell: `Investments!C${i + 2}`,
    }));

  const assets: Asset[] = assetRows
    .filter(row => row[0])
    .map((row, i) => ({
      name: row[0],
      value: parseCurrency(row[1] || '0'),
      editable: row[0] !== 'Investment Portfolio',
      cell: `Assets!B${i + 2}`,
    }));

  const retirement401k: Retirement401k[] = retirement401kRows
    .filter(row => row[0])
    .map((row, i) => ({
      fundName: row[0],
      ticker: row[1] || '',
      shares: parseFloat(row[2] || '0'),
      balance: parseCurrency(row[3] || '0'),
      employer: row[4] || '',
      accountType: row[5] || '401k Traditional',
      costBasis: parseCurrency(row[6] || '0'),
      editable: true,
      balanceCell: `'401k'!D${i + 2}`,
      sharesCell: `'401k'!C${i + 2}`,
      costBasisCell: `'401k'!G${i + 2}`,
      fundNameCell: `'401k'!A${i + 2}`,
      tickerCell: `'401k'!B${i + 2}`,
    }));

  const liabilities: Liability[] = liabilityRows
    .filter(row => row[0])
    .map((row, i) => ({
      name: row[0],
      value: parseCurrency(row[1] || '0'),
      interestRate: parseFloat(row[2] || '0') || 0,
      editable: row[0] !== 'Credit Card Debt',
      cell: `Liabilities!B${i + 2}`,
      interestRateCell: `Liabilities!C${i + 2}`,
    }));

  // Parse budget from unified batchGet
  const budgetCategories: BudgetCategory[] = budgetRows
    .filter(row => row[0])
    .map((row, i) => ({
      category: row[0],
      budgeted: parseCurrency(row[1] || '0'),
      spent: parseCurrency(row[2] || '0'),
      group: row[3] || '',
      cell: `Budget!B${i + 2}`,
      spentCell: `Budget!C${i + 2}`,
      categoryCell: `Budget!A${i + 2}`,
      groupCell: `Budget!D${i + 2}`,
    }));

  // Parse goals from unified batchGet
  const goals: Goal[] = goalRows
    .filter(row => row[0])
    .map((row, i) => ({
      name: row[0],
      targetAmount: parseCurrency(row[1] || '0'),
      currentAmount: parseCurrency(row[2] || '0'),
      targetDate: row[3] || '',
      unit: row[4] || 'USD',
      nameCell: `Goals!A${i + 2}`,
      targetAmountCell: `Goals!B${i + 2}`,
      currentAmountCell: `Goals!C${i + 2}`,
      targetDateCell: `Goals!D${i + 2}`,
      unitCell: `Goals!E${i + 2}`,
    }));

  // Parse crypto holdings (prices + period returns populated later by CoinGecko)
  const cryptoHoldings: CryptoHolding[] = cryptoRows
    .filter(row => row[0])
    .map((row, i) => ({
      symbol: row[0],
      coinGeckoId: row[1] || '',
      amount: parseFloat(row[2] || '0'),
      costBasis: parseCurrency(row[3] || '0'),
      currentPrice: 0,
      totalValue: 0,
      change24h: 0,
      dailyReturn: null,
      weeklyReturn: null,
      monthlyReturn: null,
      annualReturn: null,
      account: row[4] || 'Main',
      editable: true,
      symbolCell: `Crypto!A${i + 2}`,
      amountCell: `Crypto!C${i + 2}`,
      costBasisCell: `Crypto!D${i + 2}`,
    }));

  // Parse private equity holdings
  const privateEquityHoldings: PrivateEquity[] = privateEquityRows
    .filter(row => row[0])
    .map((row, i) => {
      const shares = parseFloat(row[2] || '0');
      const pricePerShare = parseCurrency(row[3] || '0');
      return {
        name: row[0],
        symbol: row[1] || '',
        shares,
        pricePerShare,
        totalValue: shares * pricePerShare,
        costBasis: parseCurrency(row[5] || '0'),
        account: row[6] || 'Individual',
        dailyReturn: null,
        weeklyReturn: null,
        monthlyReturn: null,
        annualReturn: null,
        editable: true,
        sharesCell: `'Private Equity'!C${i + 2}`,
        pricePerShareCell: `'Private Equity'!D${i + 2}`,
        costBasisCell: `'Private Equity'!F${i + 2}`,
        nameCell: `'Private Equity'!A${i + 2}`,
        symbolCell: `'Private Equity'!B${i + 2}`,
      };
    });

  // Parse HSA accounts
  const hsaAccounts: HSA[] = hsaRows
    .filter(row => row[0])
    .map((row, i) => ({
      name: row[0],
      balance: parseCurrency(row[1] || '0'),
      editable: true,
      cell: `HSA!B${i + 2}`,
    }));

  const creditScores: CreditScore[] = creditScoreRows
    .filter(row => row[0])
    .map((row, i) => ({
      bureau: row[0],
      score: parseInt(row[1] || '0', 10),
      editable: true,
      scoreCell: `'Credit Scores'!B${i + 2}`,
    }));

  const retirementSettingsRow = retirementSettingsRows[0];
  const retirementSettings: RetirementSettings | null = retirementSettingsRow && retirementSettingsRow[0]
    ? {
        retirementYear: parseInt(retirementSettingsRow[0] || '0', 10),
        annualSpending: parseCurrency(retirementSettingsRow[1] || '0'),
        inflationRate: parseFloat(retirementSettingsRow[2] || '3'),
        growthOutlook: retirementSettingsRow[3] || 'expected',
        enabledClasses: retirementSettingsRow[4] || 'stocks,crypto,401k,pe',
        conservativeBp: parseInt(retirementSettingsRow[5] || '0', 10),
        optimisticBp: parseInt(retirementSettingsRow[6] || '0', 10),
        cagrOverrides: retirementSettingsRow[7] || '',
        taxRate: parseFloat(retirementSettingsRow[8] || '0'),
        birthYear: parseInt(retirementSettingsRow[9] || '', 10) || null,
      }
    : null;

  const userSettingsRow = userSettingsRows[0];
  const userSettings: UserSettings = {
    dashboardView: (['overview', 'standard', 'full'].includes(userSettingsRow?.[0])
      ? userSettingsRow[0]
      : DEFAULT_DASHBOARD_VIEW) as DashboardView,
    hiddenWidgets: userSettingsRow?.[1]
      ? userSettingsRow[1].split(',').filter(Boolean) as UserSettings['hiddenWidgets']
      : [],
  };

  const marketSnapshot: MarketIndex[] = marketSnapshotRows
    .filter(row => row[0])
    .map(row => ({
      symbol: row[0],
      label: row[1] || row[0],
      price: parseCurrency(row[2] || '0'),
      change: parseCurrency(row[3] || '0'),
      changePercent: parseCurrency(row[4] || '0'),
    }));

  // Seed Market Snapshot only when empty (first-time / missing tab data).
  // Never reseed when length differs — that wiped custom tickers on every load.
  if (marketSnapshot.length === 0) {
    window.gapi.client.sheets.spreadsheets.values.update({
      spreadsheetId: sheetId,
      range: `'Market Snapshot'!A2:E${MARKET_SNAPSHOT_TICKERS.length + 1}`,
      valueInputOption: 'USER_ENTERED',
      resource: { values: buildMarketSnapshotSeed() },
    }).then(() => {
    }).catch((err: unknown) => {
      console.warn('Could not seed Market Snapshot:', err);
    });
  }

  // Parse net worth history from unified batchGet
  const netWorthHistory: NetWorthSnapshot[] = historyRows
    .filter(row => row[0])
    .map(row => ({
      date: row[0],
      netWorth: parseCurrency(row[1] || '0'),
      totalAssets: parseCurrency(row[2] || '0'),
      totalLiabilities: parseCurrency(row[3] || '0'),
      banking: parseCurrency(row[4] || '0'),
      investments: parseCurrency(row[5] || '0'),
      retirement401k: parseCurrency(row[6] || '0'),
      creditCards: parseCurrency(row[7] || '0'),
      crypto: parseCurrency(row[8] || '0'),
    }));

  // Seed default budget categories if empty
  if (budgetCategories.length === 0) {
    try {
      await seedDefaultBudgetCategories(sheetId, DEFAULT_BUDGET_CATEGORIES);
      // Re-read just the budget range
      const seedResponse = await window.gapi.client.sheets.spreadsheets.values.batchGet({
        spreadsheetId: sheetId,
        ranges: ['Budget!A2:D100'],
      });
      const seededRows = seedResponse.result.valueRanges?.[0]?.values || [];
      const seededBudget: BudgetCategory[] = seededRows
        .filter((row: string[]) => row[0])
        .map((row: string[], i: number) => ({
          category: row[0],
          budgeted: parseCurrency(row[1] || '0'),
          spent: parseCurrency(row[2] || '0'),
          group: row[3] || '',
          cell: `Budget!B${i + 2}`,
          spentCell: `Budget!C${i + 2}`,
          categoryCell: `Budget!A${i + 2}`,
          groupCell: `Budget!D${i + 2}`,
        }));
      return { banking, creditCards, investments, cryptoHoldings, retirement401k, privateEquityHoldings, hsaAccounts, creditScores, marketSnapshot, assets, liabilities, budgetCategories: seededBudget, goals, netWorthHistory, retirementSettings, userSettings, checkIns };
    } catch (error) {
      console.warn('Could not seed default budget categories:', error);
    }
  }


  return { banking, creditCards, investments, cryptoHoldings, retirement401k, privateEquityHoldings, hsaAccounts, creditScores, marketSnapshot, assets, liabilities, budgetCategories, goals, netWorthHistory, retirementSettings, userSettings, checkIns };
};

/**
 * Update a single cell value in Google Sheets
 * @param sheetId - The spreadsheet ID
 * @param range - The cell range (e.g., 'Banking!B2')
 * @param value - The value to set
 */
export const updateSheetValue = async (
  sheetId: string,
  range: string,
  value: number | string
): Promise<void> => {
  const sanitized = typeof value === 'string' ? sanitizeSheetString(value) : value;
  await withTokenRefresh(() =>
    window.gapi.client.sheets.spreadsheets.values.update({
      spreadsheetId: sheetId,
      range,
      valueInputOption: 'RAW',
      resource: { values: [[sanitized]] },
    })
  );
};

/**
 * Add a new investment to the spreadsheet
 * @param sheetId - The spreadsheet ID
 * @param symbol - Stock symbol
 * @param shares - Number of shares
 * @param costBasis - Cost per share
 * @param rowIndex - The row index to add at
 */
export const addInvestment = async (
  sheetId: string,
  symbol: string,
  shares: number,
  costBasis: number,
  rowIndex: number,
  account: string = 'Individual'
): Promise<void> => {
  const safeSymbol = validateSymbol(symbol);
  const periodFormulas = buildInvestmentPeriodReturnFormulas(rowIndex);
  await window.gapi.client.sheets.spreadsheets.values.update({
    spreadsheetId: sheetId,
    range: `Investments!A${rowIndex}:K${rowIndex}`,
    valueInputOption: 'USER_ENTERED',
    resource: {
      values: [[
        safeSymbol,
        shares,
        costBasis,
        `=GOOGLEFINANCE(A${rowIndex},"price")`,
        `=B${rowIndex}*D${rowIndex}`,
        `=IF(B${rowIndex}*C${rowIndex}>0,(E${rowIndex}-(B${rowIndex}*C${rowIndex}))/(B${rowIndex}*C${rowIndex}),0)`,
        sanitizeSheetString(account),
        ...periodFormulas,
      ]],
    },
  });
};

/**
 * Add a new bank account to the spreadsheet
 * @param sheetId - The spreadsheet ID
 * @param name - Account name
 * @param balance - Account balance
 * @param rowIndex - The row index to add at
 */
export const addBankAccount = async (
  sheetId: string,
  name: string,
  balance: number,
  rowIndex: number
): Promise<void> => {
  await window.gapi.client.sheets.spreadsheets.values.update({
    spreadsheetId: sheetId,
    range: `Banking!A${rowIndex}:B${rowIndex}`,
    valueInputOption: 'RAW',
    resource: { values: [[sanitizeSheetString(name), balance]] },
  });
};

/**
 * Add a new credit card to the spreadsheet
 * @param sheetId - The spreadsheet ID
 * @param name - Card name
 * @param balance - Current balance
 * @param limit - Credit limit
 * @param rowIndex - The row index to add at
 */
export const addCreditCard = async (
  sheetId: string,
  name: string,
  balance: number,
  limit: number,
  rowIndex: number
): Promise<void> => {
  await window.gapi.client.sheets.spreadsheets.values.update({
    spreadsheetId: sheetId,
    range: `Credit Cards!A${rowIndex}:C${rowIndex}`,
    valueInputOption: 'RAW',
    resource: { values: [[sanitizeSheetString(name), balance, limit]] },
  });
};

/**
 * Add a new asset to the spreadsheet
 * @param sheetId - The spreadsheet ID
 * @param name - Asset name
 * @param value - Asset value
 * @param rowIndex - The row index to add at
 */
export const addAsset = async (
  sheetId: string,
  name: string,
  value: number,
  rowIndex: number
): Promise<void> => {
  await window.gapi.client.sheets.spreadsheets.values.update({
    spreadsheetId: sheetId,
    range: `Assets!A${rowIndex}:B${rowIndex}`,
    valueInputOption: 'RAW',
    resource: { values: [[sanitizeSheetString(name), value]] },
  });
};

/**
 * Add a new HSA account to the spreadsheet
 */
export const addHSA = async (
  sheetId: string,
  name: string,
  balance: number,
  rowIndex: number
): Promise<void> => {
  await window.gapi.client.sheets.spreadsheets.values.update({
    spreadsheetId: sheetId,
    range: `HSA!A${rowIndex}:B${rowIndex}`,
    valueInputOption: 'RAW',
    resource: { values: [[sanitizeSheetString(name), balance]] },
  });
};

export const addCreditScore = async (
  sheetId: string,
  bureau: string,
  score: number,
  rowIndex: number
): Promise<void> => {
  await window.gapi.client.sheets.spreadsheets.values.update({
    spreadsheetId: sheetId,
    range: `'Credit Scores'!A${rowIndex}:B${rowIndex}`,
    valueInputOption: 'RAW',
    resource: { values: [[sanitizeSheetString(bureau), score]] },
  });
};

export const saveRetirementSettings = async (
  sheetId: string,
  settings: RetirementSettings
): Promise<void> => {
  await withTokenRefresh(() =>
    window.gapi.client.sheets.spreadsheets.values.batchUpdate({
      spreadsheetId: sheetId,
      resource: {
        valueInputOption: 'RAW',
        data: [
          // Header for J written on every save so sheets created before Birth Year get it too
          { range: "'Retirement Settings'!J1", values: [['Birth Year']] },
          {
            range: "'Retirement Settings'!A2:J2",
            values: [[
              settings.retirementYear,
              settings.annualSpending,
              settings.inflationRate,
              settings.growthOutlook,
              settings.enabledClasses,
              settings.conservativeBp,
              settings.optimisticBp,
              settings.cagrOverrides,
              settings.taxRate,
              settings.birthYear ?? '',
            ]],
          },
        ],
      },
    })
  );
};

export const saveUserSettings = async (
  sheetId: string,
  settings: UserSettings
): Promise<void> => {
  await withTokenRefresh(() =>
    window.gapi.client.sheets.spreadsheets.values.update({
      spreadsheetId: sheetId,
      range: "'User Settings'!A2:B2",
      valueInputOption: 'RAW',
      resource: {
        values: [[settings.dashboardView, (settings.hiddenWidgets || []).join(',')]],
      },
    })
  );
};

/**
 * Add a custom ticker to the Market Snapshot tab with GOOGLEFINANCE formulas.
 * Returns the price after retries to verify the ticker is valid.
 */
export const addMarketTicker = async (
  sheetId: string,
  symbol: string,
  label: string,
  rowIndex: number
): Promise<number> => {
  const safeSymbol = validateSymbol(symbol.toUpperCase());
  const safeLabel = sanitizeSheetString(label);
  // Prefer known Google Finance symbols (e.g. SPX → .INX) when available
  const known = MARKET_SNAPSHOT_TICKERS.find(t => t.symbol === safeSymbol);
  const gfSymbol = known?.gfSymbol || safeSymbol;
  await withTokenRefresh(() =>
    window.gapi.client.sheets.spreadsheets.values.update({
      spreadsheetId: sheetId,
      range: `'Market Snapshot'!A${rowIndex}:E${rowIndex}`,
      valueInputOption: 'USER_ENTERED',
      resource: {
        values: [[
          safeSymbol,
          safeLabel,
          `=IFERROR(GOOGLEFINANCE("${gfSymbol}","price"),0)`,
          `=IFERROR(GOOGLEFINANCE("${gfSymbol}","changepct")/100*C${rowIndex},0)`,
          `=IFERROR(GOOGLEFINANCE("${gfSymbol}","changepct"),0)`,
        ]],
      },
    })
  );
  // GOOGLEFINANCE can take several seconds — retry before treating as invalid
  let price = 0;
  for (let attempt = 0; attempt < 4; attempt++) {
    await new Promise(r => setTimeout(r, attempt === 0 ? 2500 : 1500));
    const check = await withTokenRefresh(() =>
      window.gapi.client.sheets.spreadsheets.values.batchGet({
        spreadsheetId: sheetId,
        ranges: [`'Market Snapshot'!C${rowIndex}`],
      })
    );
    price = parseCurrency(check.result.valueRanges?.[0]?.values?.[0]?.[0] || '0');
    if (price > 0) break;
  }
  return price;
};

/**
 * Add a new liability to the spreadsheet
 * @param sheetId - The spreadsheet ID
 * @param name - Liability name
 * @param value - Liability amount
 * @param rowIndex - The row index to add at
 */
export const addLiability = async (
  sheetId: string,
  name: string,
  value: number,
  interestRate: number,
  rowIndex: number
): Promise<void> => {
  await window.gapi.client.sheets.spreadsheets.values.update({
    spreadsheetId: sheetId,
    range: `Liabilities!A${rowIndex}:C${rowIndex}`,
    valueInputOption: 'RAW',
    resource: { values: [[sanitizeSheetString(name), value, interestRate]] },
  });
};

/**
 * Add a single 401k holding to the spreadsheet
 */
export const add401kHolding = async (
  sheetId: string,
  fundName: string,
  ticker: string,
  shares: number,
  balance: number,
  employer: string,
  accountType: string,
  costBasis: number,
  rowIndex: number
): Promise<void> => {
  await window.gapi.client.sheets.spreadsheets.values.update({
    spreadsheetId: sheetId,
    range: `'401k'!A${rowIndex}:G${rowIndex}`,
    valueInputOption: 'RAW',
    resource: { values: [[
      sanitizeSheetString(fundName),
      ticker ? validateSymbol(ticker) : '',
      shares,
      balance,
      sanitizeSheetString(employer),
      sanitizeSheetString(accountType),
      costBasis,
    ]] },
  });
};

/**
 * Batch add multiple investments with GOOGLEFINANCE formulas (for CSV import)
 */
export const batchAddInvestments = async (
  sheetId: string,
  investments: Array<{
    symbol: string;
    shares: number;
    costBasis: number;
    account?: string;
  }>,
  startRow: number
): Promise<void> => {
  if (investments.length === 0) return;

  const values = investments.map((inv, idx) => {
    const row = startRow + idx;
    return [
      validateSymbol(inv.symbol),
      inv.shares,
      inv.costBasis,
      `=GOOGLEFINANCE(A${row},"price")`,
      `=B${row}*D${row}`,
      `=IF(B${row}*C${row}>0,(E${row}-(B${row}*C${row}))/(B${row}*C${row}),0)`,
      sanitizeSheetString(inv.account || 'Individual'),
      ...buildInvestmentPeriodReturnFormulas(row),
    ];
  });

  const endRow = startRow + investments.length - 1;
  await window.gapi.client.sheets.spreadsheets.values.update({
    spreadsheetId: sheetId,
    range: `Investments!A${startRow}:K${endRow}`,
    valueInputOption: 'USER_ENTERED',
    resource: { values },
  });
};

/**
 * Update shares and cost basis for existing investments in-place (for CSV merge)
 */
export const batchUpdateInvestments = async (
  sheetId: string,
  updates: Array<{ row: number; shares: number; costBasis: number }>
): Promise<void> => {
  if (updates.length === 0) return;

  const data = updates.map(u => ({
    range: `Investments!B${u.row}:C${u.row}`,
    values: [[u.shares, u.costBasis]],
  }));

  await withTokenRefresh(() =>
    window.gapi.client.sheets.spreadsheets.values.batchUpdate({
      spreadsheetId: sheetId,
      resource: {
        valueInputOption: 'RAW',
        data,
      },
    })
  );
};

/**
 * Batch add multiple 401k holdings (for CSV import)
 */
export const batchAdd401kHoldings = async (
  sheetId: string,
  holdings: Array<{
    fundName: string;
    ticker: string;
    shares: number;
    balance: number;
    employer: string;
    accountType: string;
    costBasis: number;
  }>,
  startRow: number
): Promise<void> => {
  if (holdings.length === 0) return;

  const values = holdings.map(h => [
    sanitizeSheetString(h.fundName),
    h.ticker ? validateSymbol(h.ticker) : '',
    h.shares,
    h.balance,
    sanitizeSheetString(h.employer),
    sanitizeSheetString(h.accountType),
    h.costBasis,
  ]);

  const endRow = startRow + holdings.length - 1;
  await window.gapi.client.sheets.spreadsheets.values.update({
    spreadsheetId: sheetId,
    range: `'401k'!A${startRow}:G${endRow}`,
    valueInputOption: 'RAW',
    resource: { values },
  });
};

/**
 * Clear all 401k holdings data (keeps headers)
 */
export const clearAll401kHoldings = async (sheetId: string): Promise<void> => {
  // Clear data range but keep headers in row 1
  await window.gapi.client.sheets.spreadsheets.values.clear({
    spreadsheetId: sheetId,
    range: "'401k'!A2:G100",
  });
};

/**
 * Delete a row from a sheet by clearing its contents
 * @param sheetId - The spreadsheet ID
 * @param sheetName - The sheet tab name
 * @param rowIndex - The row index to delete (1-based, where row 1 is headers)
 */
export const deleteRow = async (
  sheetId: string,
  sheetName: string,
  rowIndex: number
): Promise<void> => {
  // Get the sheet ID for the batch update
  const sheetMetadata = await withTokenRefresh(() =>
    window.gapi.client.sheets.spreadsheets.get({
      spreadsheetId: sheetId,
    })
  );

  const sheet = sheetMetadata.result.sheets?.find(
    (s: { properties?: { title?: string } }) => s.properties?.title === sheetName
  );

  if (!sheet?.properties?.sheetId) {
    throw new Error(`Sheet "${sheetName}" not found`);
  }

  // Delete the row using batchUpdate
  await withTokenRefresh(() =>
    window.gapi.client.sheets.spreadsheets.batchUpdate({
      spreadsheetId: sheetId,
      resource: {
        requests: [
          {
            deleteDimension: {
              range: {
                sheetId: sheet.properties.sheetId,
                dimension: 'ROWS',
                startIndex: rowIndex - 1, // 0-based
                endIndex: rowIndex, // exclusive
              },
            },
          },
        ],
      },
    })
  );

};

/**
 * Delete multiple rows in a single batch API call.
 * Rows must be sorted descending so indices don't shift during deletion.
 */
export const deleteRows = async (
  sheetId: string,
  sheetName: string,
  rowIndices: number[]
): Promise<void> => {
  if (rowIndices.length === 0) return;

  const sheetMetadata = await withTokenRefresh(() =>
    window.gapi.client.sheets.spreadsheets.get({
      spreadsheetId: sheetId,
    })
  );

  const sheet = sheetMetadata.result.sheets?.find(
    (s: { properties?: { title?: string } }) => s.properties?.title === sheetName
  );

  if (!sheet?.properties?.sheetId) {
    throw new Error(`Sheet "${sheetName}" not found`);
  }

  // Sort descending so higher rows are deleted first (avoids index shifting)
  const sorted = [...rowIndices].sort((a, b) => b - a);

  const requests = sorted.map(rowIndex => ({
    deleteDimension: {
      range: {
        sheetId: sheet.properties.sheetId,
        dimension: 'ROWS',
        startIndex: rowIndex - 1,
        endIndex: rowIndex,
      },
    },
  }));

  await withTokenRefresh(() =>
    window.gapi.client.sheets.spreadsheets.batchUpdate({
      spreadsheetId: sheetId,
      resource: { requests },
    })
  );

};

// ========== BUDGET ==========

export const addBudgetCategory = async (
  sheetId: string,
  category: string,
  budgeted: number,
  rowIndex: number,
  group: string = ''
): Promise<void> => {
  await window.gapi.client.sheets.spreadsheets.values.update({
    spreadsheetId: sheetId,
    range: `Budget!A${rowIndex}:D${rowIndex}`,
    valueInputOption: 'RAW',
    resource: { values: [[sanitizeSheetString(category), budgeted, 0, sanitizeSheetString(group)]] },
  });
};

export const seedDefaultBudgetCategories = async (
  sheetId: string,
  categories: string[]
): Promise<void> => {
  const values = categories.map(cat => [sanitizeSheetString(cat), 0, 0, '']);
  await window.gapi.client.sheets.spreadsheets.values.update({
    spreadsheetId: sheetId,
    range: `Budget!A2:D${categories.length + 1}`,
    valueInputOption: 'RAW',
    resource: { values },
  });
};

// ========== GOALS ==========

export const addGoal = async (
  sheetId: string,
  name: string,
  targetAmount: number,
  currentAmount: number,
  targetDate: string,
  unit: string,
  rowIndex: number
): Promise<void> => {
  await window.gapi.client.sheets.spreadsheets.values.update({
    spreadsheetId: sheetId,
    range: `Goals!A${rowIndex}:E${rowIndex}`,
    valueInputOption: 'RAW',
    resource: { values: [[sanitizeSheetString(name), targetAmount, currentAmount, sanitizeSheetString(targetDate), sanitizeSheetString(unit)]] },
  });
};

// ========== CRYPTO ==========

export const addCryptoHolding = async (
  sheetId: string,
  symbol: string,
  coinGeckoId: string,
  amount: number,
  costBasis: number,
  rowIndex: number,
  account: string = 'Main'
): Promise<void> => {
  await window.gapi.client.sheets.spreadsheets.values.update({
    spreadsheetId: sheetId,
    range: `Crypto!A${rowIndex}:E${rowIndex}`,
    valueInputOption: 'RAW',
    resource: { values: [[
      sanitizeSheetString(symbol).toUpperCase(),
      coinGeckoId ? validateCryptoId(coinGeckoId) : '',
      amount,
      costBasis,
      sanitizeSheetString(account),
    ]] },
  });
};

// ========== PRIVATE EQUITY ==========

export const addPrivateEquityHolding = async (
  sheetId: string,
  name: string,
  symbol: string,
  shares: number,
  pricePerShare: number,
  costBasis: number,
  rowIndex: number,
  account: string = 'Individual'
): Promise<void> => {
  const totalValue = shares * pricePerShare;
  await window.gapi.client.sheets.spreadsheets.values.update({
    spreadsheetId: sheetId,
    range: `'Private Equity'!A${rowIndex}:G${rowIndex}`,
    valueInputOption: 'RAW',
    resource: { values: [[
      sanitizeSheetString(name),
      symbol ? sanitizeSheetString(symbol).toUpperCase() : '',
      shares,
      pricePerShare,
      totalValue,
      costBasis,
      sanitizeSheetString(account),
    ]] },
  });
};

/**
 * Create a new spreadsheet and populate it with existing data
 * Used when the original sheet was deleted
 * @param data - The current financial data to restore
 * @returns The new spreadsheet ID
 */
export const createSheetWithData = async (data: {
  banking: BankAccount[];
  creditCards: CreditCard[];
  investments: Investment[];
  cryptoHoldings?: CryptoHolding[];
  retirement401k: Retirement401k[];
  privateEquityHoldings?: PrivateEquity[];
  hsaAccounts?: HSA[];
  assets: Asset[];
  liabilities: Liability[];
}): Promise<string> => {

  // Create the base structure
  const createResponse = await window.gapi.client.sheets.spreadsheets.create({
    properties: {
      title: `TallyUp - ${new Date().toLocaleDateString()} (Restored)`,
    },
    sheets: [
      { properties: { title: 'README', gridProperties: { rowCount: 20, columnCount: 2 } } },
      { properties: { title: 'Banking', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Credit Cards', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Investments', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: '401k', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Assets', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Liabilities', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Net Worth History', gridProperties: { rowCount: 500, columnCount: 10 } } },
      { properties: { title: 'Budget', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Goals', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Crypto', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Private Equity', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'HSA', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Credit Scores', gridProperties: { rowCount: 100, columnCount: 10 } } },
      { properties: { title: 'Market Snapshot', gridProperties: { rowCount: 20, columnCount: 10 } } },
      { properties: { title: 'Retirement Settings', gridProperties: { rowCount: 5, columnCount: 10 } } },
      { properties: { title: 'User Settings', gridProperties: { rowCount: 5, columnCount: 10 } } },
    ],
  });

  const newSheetId = createResponse.result.spreadsheetId;

  // Add headers
  await window.gapi.client.sheets.spreadsheets.values.batchUpdate({
    spreadsheetId: newSheetId,
    resource: {
      valueInputOption: 'RAW',
      data: [
        {
          range: 'README!A1:A12',
          values: [
            ['📊 TallyUp Finance — Your Financial Data'],
            [''],
            ['⚠️ Please do not edit this spreadsheet directly.'],
            [''],
            ['TallyUp reads from and writes to this sheet automatically as you use the app.'],
            ['Manual edits may cause data corruption, broken formulas, or unexpected behavior in the dashboard.'],
            [''],
            ['✅ Use the TallyUp app to add, edit, or delete entries: https://www.tallyup.finance'],
            [''],
            ['🔒 Your data lives here in your Google Drive — TallyUp never sees or stores your information on any server.'],
            [''],
            ['Questions or issues? Submit feedback from Settings in the app, or email support@tallyup.finance'],
          ],
        },
        { range: 'Banking!A1:B1', values: [['Account Name', 'Balance']] },
        { range: 'Credit Cards!A1:C1', values: [['Card Name', 'Balance', 'Limit']] },
        { range: 'Investments!A1:K1', values: [INVESTMENT_HEADERS] },
        { range: "'401k'!A1:G1", values: [['Fund Name', 'Ticker', 'Shares', 'Balance', 'Employer', 'Account Type', 'Cost Basis']] },
        { range: 'Assets!A1:B1', values: [['Asset Name', 'Value']] },
        { range: 'Liabilities!A1:C1', values: [['Liability Name', 'Amount', 'APR %']] },
        { range: "'Net Worth History'!A1:I1", values: [NET_WORTH_HISTORY_HEADERS] },
        { range: 'Budget!A1:D1', values: [BUDGET_HEADERS] },
        { range: 'Goals!A1:E1', values: [GOALS_HEADERS] },
        { range: 'Crypto!A1:E1', values: [CRYPTO_HEADERS] },
        { range: "'Private Equity'!A1:G1", values: [PRIVATE_EQUITY_HEADERS] },
        { range: 'HSA!A1:B1', values: [HSA_HEADERS] },
        { range: "'Credit Scores'!A1:B1", values: [CREDIT_SCORE_HEADERS] },
        { range: "'Market Snapshot'!A1:E1", values: [MARKET_SNAPSHOT_HEADERS] },
        { range: "'Retirement Settings'!A1:J1", values: [RETIREMENT_SETTINGS_HEADERS] },
        { range: "'User Settings'!A1:A1", values: [USER_SETTINGS_HEADERS] },
      ],
    },
  });

  // Prepare data arrays
  const bankingValues = data.banking.map(b => [b.name, b.balance]);
  const creditCardValues = data.creditCards.map(c => [c.name, c.balance, c.limit]);
  const investmentValues = data.investments.map((inv, idx) => {
    const row = idx + 2;
    return [
      inv.symbol,
      inv.shares,
      inv.costBasis,
      `=GOOGLEFINANCE(A${row},"price")`,
      `=B${row}*D${row}`,
      `=IF(B${row}*C${row}>0,(E${row}-(B${row}*C${row}))/(B${row}*C${row}),0)`,
      inv.account || 'Individual',
      ...buildInvestmentPeriodReturnFormulas(row),
    ];
  });

  // 401k holdings
  const retirement401kValues = data.retirement401k.map(h => [
    h.fundName, h.ticker, h.shares, h.balance, h.employer, h.accountType, h.costBasis,
  ]);

  // For assets, handle the Investment Portfolio formula
  const assetValues = data.assets.map(a => {
    if (a.name === 'Investment Portfolio') {
      return [a.name, '=SUM(Investments!E:E)'];
    }
    return [a.name, a.value];
  });

  // For liabilities, handle the Credit Card Debt formula
  const liabilityValues = data.liabilities.map(l => {
    if (l.name === 'Credit Card Debt') {
      return [l.name, "=SUM('Credit Cards'!B:B)", l.interestRate || 0];
    }
    return [l.name, l.value, l.interestRate || 0];
  });

  // Build batch update data
  const batchData: { range: string; values: (string | number)[][] }[] = [];

  if (bankingValues.length > 0) {
    batchData.push({
      range: `Banking!A2:B${bankingValues.length + 1}`,
      values: bankingValues,
    });
  }

  if (creditCardValues.length > 0) {
    batchData.push({
      range: `Credit Cards!A2:C${creditCardValues.length + 1}`,
      values: creditCardValues,
    });
  }

  if (investmentValues.length > 0) {
    batchData.push({
      range: `Investments!A2:K${investmentValues.length + 1}`,
      values: investmentValues,
    });
  }

  if (retirement401kValues.length > 0) {
    batchData.push({
      range: `'401k'!A2:G${retirement401kValues.length + 1}`,
      values: retirement401kValues,
    });
  }

  if (assetValues.length > 0) {
    batchData.push({
      range: `Assets!A2:B${assetValues.length + 1}`,
      values: assetValues,
    });
  }

  if (liabilityValues.length > 0) {
    batchData.push({
      range: `Liabilities!A2:C${liabilityValues.length + 1}`,
      values: liabilityValues,
    });
  }

  // Crypto holdings
  const cryptoValues = (data.cryptoHoldings || []).map(c => [
    c.symbol, c.coinGeckoId, c.amount, c.costBasis, c.account || 'Main',
  ]);
  if (cryptoValues.length > 0) {
    batchData.push({
      range: `Crypto!A2:E${cryptoValues.length + 1}`,
      values: cryptoValues,
    });
  }

  // Private equity holdings
  const privateEquityValues = (data.privateEquityHoldings || []).map(h => [
    h.name, h.symbol, h.shares, h.pricePerShare, h.totalValue, h.costBasis, h.account || 'Individual',
  ]);
  if (privateEquityValues.length > 0) {
    batchData.push({
      range: `'Private Equity'!A2:G${privateEquityValues.length + 1}`,
      values: privateEquityValues,
    });
  }

  // HSA accounts
  const hsaValues = (data.hsaAccounts || []).map(h => [h.name, h.balance]);
  if (hsaValues.length > 0) {
    batchData.push({
      range: `HSA!A2:B${hsaValues.length + 1}`,
      values: hsaValues,
    });
  }

  // Market Snapshot seed formulas
  batchData.push({
    range: `'Market Snapshot'!A2:E${MARKET_SNAPSHOT_TICKERS.length + 1}`,
    values: buildMarketSnapshotSeed(),
  });

  // Write all data
  if (batchData.length > 0) {
    await window.gapi.client.sheets.spreadsheets.values.batchUpdate({
      spreadsheetId: newSheetId,
      resource: {
        valueInputOption: 'USER_ENTERED',
        data: batchData,
      },
    });
  }


  // Style the spreadsheet
  await styleTemplateSheet(newSheetId);

  return newSheetId;
};
